import { useModal } from '@europrocurement/flexy-components';
import { Catalogs, useOffreService } from '@europrocurement/l2d-domain';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
    CatalogueCatalogueRead,
    PackagePackageRead,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { Menu, MenuItem } from '@mui/material';
import { castIriToId } from '@europrocurement/l2d-utils';
import { AnyAction } from 'redux';
import FormCatalogue from '../../../Offres/Catalog/Form/Form';
import FormPackage from '../../../Offres/Package/Form/Form';
import { PrescriberArticleOverrideForm } from '../../../Offres/Prescriber';
import { OffrePrescripteurRelArticle } from '../../hook/useOffreForm';

const ActionMenuItems: React.FunctionComponent<{
    showPrescripteur: boolean;
    showCatalogue: boolean;
    showPackage: boolean;
    catalogue: Catalogs | null;
    open: boolean;
    idPrescriber?: number;
    close: () => void;
    anchorEl: HTMLElement | null;
}> = function ({
    showPrescripteur = false,
    showCatalogue = false,
    showPackage = false,
    idPrescriber = undefined,
    catalogue = null,
    open = false,
    close = null,
    anchorEl = null,
}) {
    const dispatch = useDispatch();
    const { catalogsFullSlice, getCatalogsFull } = useOffreService();
    const { modalActions } = useModal();

    const reloadList = () => {
        setTimeout(() => {
            dispatch(getCatalogsFull({}) as unknown as AnyAction);
            modalActions.close();
        }, 2000);
    };

    const onUpdate = (updatedItems: OffrePrescripteurRelArticle[]) => {
        modalActions.close();

        if (catalogue) {
            const newSurchargeIds = updatedItems.map((item) => item.id);
            const rubrique = catalogue.infosTarif.rubriques.VAC || {
                articles: {},
                totalRubFac: 0,
            };
            const vacArticlesIds = Object.keys(rubrique.articles).map((artId) => Number(artId));
            const surcharges = [
                ...(catalogue?.surchargePrescripteur || []).filter(
                    (surcharge: OffrePrescripteurRelArticle) =>
                        !newSurchargeIds.includes(surcharge.id),
                ),
                ...updatedItems,
            ];
            const totalRubFac = updatedItems.reduce((accumulator, item) => {
                const articleId = item.article?.id
                    ? item.article?.id
                    : castIriToId(item.article as string);
                const currentTarif = vacArticlesIds.includes(articleId) ? Number(item.tarif) : 0;

                return accumulator + currentTarif;
            }, 0);

            const articlesProxy = { ...rubrique.articles };
            updatedItems.forEach((item) => {
                const articleId = item.article?.id
                    ? item.article?.id
                    : castIriToId(item.article as string);

                if (articlesProxy[articleId]) {
                    articlesProxy[articleId] = {
                        ...articlesProxy[articleId],
                        isSurchargePrescripteur: true,
                    };
                }
            });

            dispatch(
                catalogsFullSlice.actions.updatemainItem({
                    id: catalogue.id,
                    changes: {
                        ...catalogue,
                        infosTarif: {
                            ...catalogue.infosTarif,
                            rubriques: {
                                ...catalogue.infosTarif.rubriques,
                                VAC: {
                                    ...rubrique,
                                    tarifTotalRubFac: totalRubFac,
                                    articles: articlesProxy,
                                },
                            },
                        },
                        surchargePrescripteur: surcharges,
                    },
                }),
            );
        }
    };

    const onClear = () => {
        modalActions.close();

        if (catalogue) {
            const rubrique = catalogue.infosTarif.rubriques.VAC || {
                articles: {},
                totalRubFac: 0,
            };
            const articlesProxy = { ...rubrique.articles };

            const totalRubFac = Object.values(articlesProxy).reduce((accumulator: number, item) => {
                const currentTarif = item?.tarif ? Number(item.tarif) : 0;

                return accumulator + currentTarif;
            }, 0);

            Object.keys(articlesProxy).forEach((item) => {
                articlesProxy[item] = {
                    ...(rubrique.articles[item] || {}),
                    isSurchargePrescripteur: false,
                };
            });

            dispatch(
                catalogsFullSlice.actions.updatemainItem({
                    id: catalogue.id,
                    changes: {
                        ...catalogue,
                        infosTarif: {
                            ...catalogue.infosTarif,
                            rubriques: {
                                ...catalogue.infosTarif.rubriques,
                                VAC: {
                                    ...rubrique,
                                    articles: articlesProxy,
                                    tarifTotalRubFac: totalRubFac,
                                },
                            },
                        },
                        surchargePrescripteur: [],
                    },
                }),
            );
        }
    };

    const handleClose = () => {
        if (close) {
            close();
        }
    };
    const handlePrescriber = () => {
        if (!catalogue || !catalogue.id) {
            throw new Error('missing catalogue');
        }
        handleClose();
        modalActions.call(
            <PrescriberArticleOverrideForm
                entity={catalogue}
                idCatalogue={catalogue.id}
                isCreate={false}
                onUpdate={onUpdate}
                onClear={onClear}
                afterSubmit={reloadList}
                idPrescriber={idPrescriber}
            />,
        );
    };

    const handleCatalogue = () =>
        modalActions.call(
            <FormCatalogue
                entity={catalogue as CatalogueCatalogueRead}
                isCreate={false}
                afterSubmit={reloadList}
            />,
        );

    const handlePackage = () =>
        modalActions.call(
            <FormPackage
                entity={catalogue?.package as PackagePackageRead}
                isCreate={false}
                afterSubmit={reloadList}
            />,
        );

    return (
        <Menu
            id={`${catalogue?.id}-catalogue-long-menu`}
            MenuListProps={{
                'aria-labelledby': 'long-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
        >
            {showPrescripteur ? (
                <MenuItem
                    key={`${catalogue?.id}-catalogue-prescripteur`}
                    onClick={handlePrescriber}
                >
                    Modification d&apos;honoraires
                </MenuItem>
            ) : null}
            {showCatalogue ? (
                <MenuItem
                    key={`${catalogue?.id}-catalogue-catalogue`}
                    onClick={handleCatalogue}
                >
                    Surcharge Catalogue
                </MenuItem>
            ) : null}
            {showPackage ? (
                <MenuItem
                    key={`${catalogue?.id}-catalogue-package`}
                    onClick={handlePackage}
                >
                    Surcharge Package
                </MenuItem>
            ) : null}
        </Menu>
    );
};
export default ActionMenuItems;
